import { Ref } from 'vue';
import { inputField } from '@/ui/components/form';
import { Task, useValidation } from '@/ui/concerns';
import { EntityFolderEdit, EntityFolder, validate } from '@/entities/entityFolder';
import { apiErrorMessage } from '@/ui/components';
import { t } from '@/ui/i18n/i18n';
import { SelectOptions, SelectOptionsWithId } from '@/ui/types';
import { chooseEntityItemField } from '@/ui/components/entityItem/ChooseEntityItemField';
import { chooseEntityFolderField } from '@/ui/components/entityFolder/ChooseEntityFolderField';
import { chooseEntityFolderLabelField } from '@/ui/components/entityFolderLabel/ChooseEntityFolderLabelField';
import { chooseUserField } from '@/ui/components/user/ChooseUserField';

export function useEntityFolderForm(record: Ref<EntityFolder | EntityFolderEdit>, task: Task, removeLabel: Function) {
  const {
    validationErrors,
    hasNoValidationErrors,
    showValidationErrors,
  } = useValidation(validate, record, task.state);

  const handleSubmit = () => {
    showValidationErrors.value = true;
    if (hasNoValidationErrors.value) {
      task.run();
    }
  };

  const formFields = (
    entityItemOptions: SelectOptions, childrenOptions: SelectOptions, userOptions: SelectOptions,
    labelOptions: SelectOptionsWithId<string>, fetchSelectOptions: (queryData: Ref) => Task,
  ) => {
    return [
      inputField({
        name: 'title',
        label: t('entity-folder.title'),
        placeholder: t('entity-folder.enter-title'),
        value: record.value.title,
        required: true,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },
      value => {
        record.value.title = value;
      }),

      chooseEntityItemField({
        props: {
          value: record.value.entityItems,
          options: entityItemOptions,
          errors: validationErrors.value,
          disabled: task.state.value.pending,
          required: false,
          placeholder: t('entity-folder.enter-entity-items'),
        },
        on: {
          input: (value: number[]) => {
            record.value.entityItems = value;
          },
        },
      }),

      chooseEntityFolderField({
        props: {
          value: record.value.children,
          options: childrenOptions,
          errors: validationErrors.value,
          disabled: task.state.value.pending,
          name: 'children',
          required: false,
          tooltipText: t('entity-folder.tooltips.children'),
          label: t('entity-folder.children'),
          placeholder: t('entity-folder.placeholders.children'),
        },
        on: {
          input: (value: number[]) => {
            record.value.children = value;

            fetchSelectOptions(record).run();
          },
        },
      }),

      chooseUserField({
        props: {
          value: record.value.users,
          options: userOptions,
          errors: validationErrors.value,
          disabled: task.state.value.pending,
          required: false,
          label: t('entity-folder.users'),
          placeholder: t('entity-folder.placeholders.users'),
        },
        on: {
          input: (value: number[]) => {
            record.value.users = value;
          },
        },
      }),

      chooseEntityFolderLabelField({
        props: {
          value: record.value.labels,
          options: labelOptions,
          errors: validationErrors.value,
          disabled: task.state.value.pending,
          required: false,
          tooltipText: t('entity-folder.tooltips.labels'),
          label: t('entity-folder.labels'),
          placeholder: t('entity-folder.placeholders.labels'),
        },
        on: {
          // @ts-ignore
          removeLabel: ({ value }) => {
            removeLabel(value)();
          },
          input: (value: string[]) => {
            record.value.labels = value;
          },
        },
      }),

      apiErrorMessage(task.state.value),
    ];
  };

  return {
    formFields,
    handleSubmit,
  };
}
